import { useForm } from "react-hook-form";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { useMutation } from "@tanstack/react-query";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2Icon } from "lucide-react";
import { useEffect } from "react";
import { authClient, SessionPayload } from "@/lib/auth/token-manager";
import { newPasswordSchema } from "@/lib/validation/password";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { PasswordInput } from "../../ui/password-input";
import { captureEvent, sanitizeErrorReason } from "@/lib/analytics";

const passwordSchema = z.object({
  password: newPasswordSchema,
  token: z.string(),
});

export type AuthenticateForm = {
  token: string;
  password: string;
};

export const AuthenticationReset = ({
  onLogin,
  goToLogin,
  token,
}: {
  onLogin: (data: SessionPayload) => void;
  goToLogin: () => void;
  token?: string;
}) => {
  const { isPending, isError, error, mutate } = useMutation({
    mutationKey: ["login/register"],
    mutationFn: async ({ token, password }: AuthenticateForm) => {
      const res = await authClient
        .post(`/auth/reset-password`, {
          token,
          password,
        })
        .catch(err => {
          throw new Error(
            err.response?.data.message
              ? err.response?.data.message
              : "Something went wrong, please try again.",
          );
        });

      return res.data.data;
    },
    onSuccess: res => {
      captureEvent("onboarding_reset_password_succeeded", {
        method: "token",
      });
      onLogin(res);
    },
    onError: err => {
      captureEvent("onboarding_reset_password_failed", {
        method: "token",
        reason: sanitizeErrorReason(err),
      });
    },
  });

  const form = useForm<AuthenticateForm>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      token: token || "",
      password: "",
    },
  });

  const handleSubmit = (values: AuthenticateForm) => {
    captureEvent("onboarding_reset_password_submitted", {
      method: "token",
    });
    mutate({ ...values });
  };

  useEffect(() => {
    if (!token) {
      goToLogin();
    }
  }, [token, goToLogin]);

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="flex flex-grow flex-col justify-between"
      >
        <div className="flex flex-grow flex-col gap-5">
          <FormField
            name="password"
            control={form.control}
            render={({ field, fieldState: { error } }) => (
              <FormItem>
                <FormLabel>Password</FormLabel>
                <PasswordInput
                  value={field.value}
                  onValueChange={field.onChange}
                />
                {error && <FormMessage>{error.message}</FormMessage>}
              </FormItem>
            )}
          />
        </div>
        <div className="flex flex-grow flex-col justify-end gap-4">
          {isError ? (
            <Alert variant="destructive" className="-mb-8 border-none p-0">
              <AlertDescription>
                {error?.message || "An error occurred, please try again."}
              </AlertDescription>
            </Alert>
          ) : null}
          <Button
            type="submit"
            variant="default"
            disabled={isPending}
            className="mt-6 h-auto rounded-xl py-3"
          >
            {isPending ? (
              <>
                <Loader2Icon className="mr-2 h-5 w-5 animate-spin" />
                Loading...
              </>
            ) : (
              "Reset Password"
            )}
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="-mt-2"
            onClick={goToLogin}
          >
            Back to Login
          </Button>
        </div>
      </form>
    </Form>
  );
};
